import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw,
  Layout,
  Eye,
  CheckCircle,
  Clock,
  Search,
  Plus,
  Globe,
  MessageSquare,
  MoreVertical,
  Activity,
  X,
  Trash2,
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import MetaTemplateBuilder from './MetaTemplateBuilder';

// Mirrors MetaTemplateService.previewTemplate's server-side logic — done
// client-side here since the components are already in hand, no round-trip needed.
function buildPreview(template) {
  const preview = { header: null, body: '', footer: null, buttons: [] };
  (template.components || []).forEach((comp) => {
    if (comp.type === 'BODY') {
      preview.body = (comp.text || '').replace(/\{\{\d+\}\}/g, '[Variable]');
    } else if (comp.type === 'HEADER') {
      preview.header = { format: comp.format, text: comp.text ? comp.text.replace(/\{\{\d+\}\}/g, '[Variable]') : null };
    } else if (comp.type === 'FOOTER') {
      preview.footer = comp.text;
    } else if (comp.type === 'BUTTONS') {
      preview.buttons = comp.buttons || [];
    }
  });
  return preview;
}

// Meta's message_template_status_update webhook only ever sends a coarse
// category code (e.g. "TAG_CONTENT_MISMATCH"), never a longer human
// explanation — this is the full detail Meta provides, there is no richer
// message to fetch. Mapping the known codes to plain-English + a concrete
// fix turns that bare code into something actually actionable.
const REJECTION_REASON_INFO = {
  INVALID_FORMAT: {
    label: 'Invalid Format',
    explanation: "The template's structure doesn't match what Meta expects for its category — e.g. an AUTHENTICATION template with custom body text, or a malformed variable/button.",
    fix: 'Re-check the template against category rules (Authentication templates cannot have custom body/footer text or a header) and resubmit.',
  },
  TAG_CONTENT_MISMATCH: {
    label: 'Category Mismatch',
    explanation: "The content doesn't match the category it was submitted under — e.g. promotional wording submitted as UTILITY, or transactional wording submitted as MARKETING.",
    fix: 'Either rewrite the copy to match the chosen category, or resubmit under the correct category.',
  },
  INCORRECT_CATEGORY: {
    label: 'Incorrect Category',
    explanation: 'Meta determined this template belongs in a different category than the one it was submitted under.',
    fix: 'Resubmit the same content under the category Meta suggests (visible in WhatsApp Manager for this template).',
  },
  ABUSIVE_CONTENT: {
    label: 'Abusive Content',
    explanation: 'Meta flagged the wording as spammy, deceptive, or otherwise against WhatsApp Business Policy.',
    fix: 'Rewrite the message to remove promotional pressure tactics, misleading claims, or policy-violating language, then resubmit.',
  },
  SCAM: {
    label: 'Flagged As Scam',
    explanation: 'Meta flagged this template as resembling a scam/phishing pattern.',
    fix: 'Remove any urgency/financial-request language, verify sender details are accurate, and resubmit.',
  },
  INVALID_PHONE_NUMBER: {
    label: 'Invalid Phone Number',
    explanation: 'A phone-number button on this template has an invalid or unreachable number.',
    fix: 'Fix the phone number button (include country code) and resubmit.',
  },
  NONE: {
    label: 'No Reason Given',
    explanation: "Meta didn't attach a specific reason code to this status change.",
    fix: 'Check WhatsApp Manager → Message Templates for this template directly — it sometimes shows more context there than the webhook provides.',
  },
};

function RejectionDetailsModal({ template, onClose }) {
  const info = REJECTION_REASON_INFO[template.statusReason] || {
    label: template.statusReason,
    explanation: "Meta didn't provide more detail beyond this code — this is the complete reason available from their API.",
    fix: 'Check WhatsApp Manager → Message Templates for this template for any additional context Meta may show there.',
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[100dvh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#D8DDE6] flex items-center justify-between bg-[#FEF2F2]">
          <div>
            <div className="text-[13px] font-bold text-[#080707]">{template.name}</div>
            <div className="text-[11px] text-[#BA0517] font-bold uppercase tracking-wide">{template.status}</div>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wide mb-1">Meta's Reason Code</p>
            <p className="text-[13px] font-mono font-bold text-[#080707]">{template.statusReason || 'NONE'} — {info.label}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wide mb-1">What This Means</p>
            <p className="text-[13px] text-[#080707] leading-relaxed">{info.explanation}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wide mb-1">What To Do</p>
            <p className="text-[13px] text-[#080707] leading-relaxed">{info.fix}</p>
          </div>
          {template.statusUpdatedAt && (
            <p className="text-[11px] text-[#706E6B]">Updated {new Date(template.statusUpdatedAt).toLocaleString()}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplatePreviewModal({ template, onClose }) {
  const preview = buildPreview(template);
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-sm z-10 max-h-[100dvh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-[#D8DDE6] flex items-center justify-between bg-[#F4F6F9]">
          <div>
            <div className="text-[13px] font-bold text-[#080707]">{template.name}</div>
            <div className="text-[11px] text-[#706E6B] uppercase">{template.language} · {template.category}</div>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>
        <div className="p-4 bg-[#E5DDD5]">
          <div className="bg-white rounded-lg shadow-sm p-3 max-w-[260px]">
            {preview.header?.text && <p className="text-[13px] font-bold text-[#080707] mb-1">{preview.header.text}</p>}
            {preview.header?.format && preview.header.format !== 'TEXT' && !preview.header.text && (
              <div className="bg-[#F4F6F9] rounded h-24 flex items-center justify-center text-[11px] text-[#706E6B] mb-1 uppercase">
                {preview.header.format}
              </div>
            )}
            <p className="text-[13px] text-[#080707] whitespace-pre-wrap">{preview.body}</p>
            {preview.footer && <p className="text-[11px] text-[#706E6B] mt-1">{preview.footer}</p>}
          </div>
          {preview.buttons.length > 0 && (
            <div className="mt-2 max-w-[260px] space-y-1">
              {preview.buttons.map((b, i) => (
                <div key={i} className="bg-white rounded-md shadow-sm py-2 text-center text-[13px] font-semibold text-[#0176D3]">
                  {b.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MetaTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [rejectionDetailsTemplate, setRejectionDetailsTemplate] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      alertService.error('Fetch Error', 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      alertService.toast('info', 'Syncing templates with Meta…');
      const res = await api.post('/meta/templates/sync');
      alertService.toast('success', `Sync completed. ${res.data.synced} templates updated.`);
      fetchTemplates();
    } catch (err) {
      alertService.error('Sync Failed', 'Could not connect to Meta.');
    } finally {
      setSyncing(false);
    }
  }, [fetchTemplates]);

  const handleDeleteTemplate = async (template) => {
    setOpenMenuId(null);
    if (!(await alertService.confirm('Delete?', `Delete template "${template.name}"? This removes it from Meta and cannot be undone.`, 'danger', 'Delete'))) return;
    setDeletingId(template._id);
    try {
      await api.delete(`/meta/templates/${template._id}`);
      alertService.toast('success', 'Template deleted.');
      setTemplates((prev) => prev.filter((t) => t._id !== template._id));
    } catch (err) {
      alertService.error('Delete Failed', err.response?.data?.error || 'Could not delete template.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      await api.post('/meta/templates', templateData);
      alertService.toast('success', 'Template submitted for Meta review.');
      setShowBuilder(false);
      fetchTemplates();
    } catch (err) {
      const d = err.response?.data || {};
      alertService.errorDetails('Submission Error', d.error || 'Template validation failed.', {
        code: d.code, subcode: d.subcode, type: d.type, fbtraceId: d.fbtraceId,
      });
      throw err;
    }
  };

  const handleUpdateTemplate = async (templateData) => {
    try {
      await api.put(`/meta/templates/${editingTemplate._id}`, templateData);
      alertService.toast('success', 'Template updated and re-submitted for Meta review.');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err) {
      const d = err.response?.data || {};
      alertService.errorDetails('Update Error', d.error || 'Could not update template.', {
        code: d.code, subcode: d.subcode, type: d.type, fbtraceId: d.fbtraceId,
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = [
    { label: 'Templates', value: templates.length, icon: Globe, color: 'text-[#0176D3]' },
    { label: 'Approved', value: templates.filter(t => t.status === 'APPROVED').length, icon: CheckCircle, color: 'text-[#2E844A]' },
    { label: 'Pending Review', value: templates.filter(t => t.status === 'PENDING').length, icon: Clock, color: 'text-[#FF9900]' },
    { label: 'Connection', value: 'Live', icon: Activity, color: 'text-[#0176D3]' },
  ];

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'sf-badge-success';
      case 'PENDING': return 'sf-badge-warning';
      case 'REJECTED': return 'sf-badge-error';
      default: return 'sf-badge-neutral';
    }
  };

  return (
    <DashboardModuleShell
      title="Meta Messaging Templates"
      subtitle="Create and manage WhatsApp Business templates, synchronized with your Meta account."
      icon={Layout}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <div className="relative group hidden lg:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#706E6B] group-focus-within:text-[#0176D3]" />
            <input
              type="text"
              placeholder="Search templates…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 pl-10 pr-4 bg-white border border-[#D8DDE6] rounded-md text-[13px] font-medium outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all w-64"
            />
          </div>
          <Button variant="outline" icon={RefreshCw} onClick={handleSync} loading={syncing}>Sync</Button>
          <Button onClick={() => setShowBuilder(true)} icon={Plus}>New Template</Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Filter Bar */}
        <div className="flex items-center gap-2 pb-1 overflow-x-auto no-scrollbar">
          {['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION'].map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={cn(
                "h-9 px-4 rounded-md text-[12px] font-bold uppercase tracking-wide transition-all whitespace-nowrap border",
                selectedCategory === cat
                  ? "bg-[#0176D3] border-[#0176D3] text-white"
                  : "bg-white border-[#D8DDE6] text-[#706E6B] hover:border-[#0176D3] hover:text-[#0176D3]"
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="sf-card flex flex-col items-center justify-center py-24">
            <RefreshCw className="w-8 h-8 animate-spin text-[#0176D3] mb-4" />
            <p className="text-[13px] font-bold text-[#706E6B]">Loading templates…</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="sf-card flex flex-col items-center justify-center py-24">
            <div className="w-16 h-16 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center mb-4">
              <Layout className="w-8 h-8 text-[#706E6B]" />
            </div>
            <h3 className="text-[15px] font-bold text-[#080707] mb-1">No templates found</h3>
            <p className="text-[13px] text-[#706E6B] mb-6 max-w-sm text-center">
              We couldn't find any messaging templates. Sync your Meta Business Account or create a new template to get started.
            </p>
            <Button variant="outline" onClick={handleSync} icon={RefreshCw}>Sync Now</Button>
          </div>
        ) : (
          <div className="sf-table-container overflow-x-auto">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Language</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Preview</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(t => (
                  <tr key={t._id} className="group">
                    <td>
                      <div className="flex items-center gap-2">
                        <MessageSquare size={14} className="text-[#0176D3] shrink-0" />
                        <span className="font-bold text-[#080707]" title={t.name}>{t.name}</span>
                      </div>
                    </td>
                    <td className="text-[#706E6B] font-medium uppercase">{t.language}</td>
                    <td className="text-[#706E6B] font-medium uppercase">{t.category}</td>
                    <td>
                      <span className={cn("sf-badge", getStatusBadgeClass(t.status))} title={t.statusReason || ''}>{t.status}</span>
                      {/* Previously a REJECTED/PAUSED/DISABLED template only ever showed the
                          bare badge — Meta's actual reason (populated by the
                          message_template_status_update webhook) is now available and
                          shown here so it's clear what needs fixing, not just that it failed. */}
                      {t.statusReason && (
                        <button
                          type="button"
                          onClick={() => setRejectionDetailsTemplate(t)}
                          className="block text-[11px] text-[#EA001E] font-bold mt-1 hover:underline text-left"
                        >
                          {t.statusReason} — View Details
                        </button>
                      )}
                    </td>
                    <td className="max-w-xs">
                      <p className="text-[12px] text-[#706E6B] truncate">
                        {t.components.find(c => c.type === 'BODY')?.text}
                      </p>
                    </td>
                    <td className="text-right">
                      <div className="relative flex items-center justify-end gap-1">
                        <button
                          title="View"
                          aria-label="View template"
                          onClick={() => setPreviewTemplate(t)}
                          className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          title="More options"
                          aria-label="More options"
                          onClick={() => setOpenMenuId(openMenuId === t._id ? null : t._id)}
                          disabled={deletingId === t._id}
                          className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors disabled:opacity-50"
                        >
                          {deletingId === t._id ? <RefreshCw size={14} className="animate-spin" /> : <MoreVertical size={14} />}
                        </button>
                        {openMenuId === t._id && (
                          <div ref={menuRef} className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#D8DDE6] rounded-md shadow-[0_2px_8px_rgba(0,0,0,0.16)] z-20 py-1">
                            <button
                              onClick={() => { setOpenMenuId(null); setEditingTemplate(t); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-[#080707] hover:bg-[#F4F6F9] transition-colors"
                            >
                              <Layout size={13} /> Edit Design
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(t)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold text-[#BA0517] hover:bg-[#FDE7E9] transition-colors"
                            >
                              <Trash2 size={13} /> Delete Template
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBuilder && (
        <MetaTemplateBuilder
          onClose={() => setShowBuilder(false)}
          onSave={handleCreateTemplate}
        />
      )}

      {editingTemplate && (
        <MetaTemplateBuilder
          editMode
          initialTemplate={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={handleUpdateTemplate}
        />
      )}

      {previewTemplate && (
        <TemplatePreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}

      {rejectionDetailsTemplate && (
        <RejectionDetailsModal template={rejectionDetailsTemplate} onClose={() => setRejectionDetailsTemplate(null)} />
      )}
    </DashboardModuleShell>
  );
};

export default MetaTemplatesPage;
